import { useCallback } from "react";
import { useSelector } from "react-redux";
import { message } from "antd";
import { useLocation } from "react-router-dom";

/**
 * Hook that returns a permission-checking function scoped to the current user's menu features.
 *
 * Usage:
 *   const canUseFeature = useFeatureCheck();
 *   if (!canUseFeature("ORDER_DELETE", "OrderRecall")) return;
 */
const useFeatureCheck = () => {

    const { menuList = [] } = useSelector((state) => state.authReducer || {});
    const location = useLocation();

    const currentMenuKey =
        location?.pathname
            ?.split("/")
            .filter(Boolean)
            .pop()
            ?.toLowerCase() || "";

    const checkFeature = useCallback(
        (featureConstantValue = "", menuUrlOrName = "", options = {}) => {
            const { showErrorMessage = true } = options;

            if (!featureConstantValue) {
                return true;
            }

            const normalizedFeature = featureConstantValue.trim().toUpperCase();
            const normalizedMenuKey =
                menuUrlOrName?.trim().toLowerCase() || currentMenuKey;

            const list = Array.isArray(menuList) ? menuList : [];

            if (!list.length) {
                if (showErrorMessage) {
                    message.error("Unable to verify permissions. Please re-login.");
                }
                return false;
            }

            const scopedMenus = normalizedMenuKey
                ? list.filter((menu) => {
                    const name = menu?.Menu_Name?.trim().toLowerCase();
                    const url = menu?.Menu_URL?.trim().toLowerCase();
                    return name === normalizedMenuKey || url === normalizedMenuKey;
                })
                : list;

            if (!scopedMenus.length) {
                if (showErrorMessage) {
                    message.error("Permission settings for this page are missing.");
                }
                return false;
            }

            const requiresFeatureCheck = scopedMenus.some(
                (menu) => menu?.FeatureExist === false
            )
                ? false
                : true;

            if (!requiresFeatureCheck) {
                return true;
            }

            const normalizedFeatures = scopedMenus
                .map((menu) => menu?.Features || "")
                .join(",")
                .split(",")
                .map((feature) => feature.trim().toUpperCase())
                .filter(Boolean);

            const hasAccess = normalizedFeatures.includes(normalizedFeature);

            if (!hasAccess && showErrorMessage) {
                message.error("You don't have permission to perform this action.");
            }

            return hasAccess;
        },
        [menuList, currentMenuKey]
    );

    return checkFeature;
};

export default useFeatureCheck;
